import { UserSelector } from '@client/components/_shared/selectors/users/UserSelector';
import { UserSelectorMultiple } from '@client/components/_shared/selectors/users/UserSelectorMultiple';
import { memo } from 'react';
import { operatorSupportsArrayValue } from '../core/operators';
import type { FilterOperator } from '../core/types';

type UserFilterValue = bigint | bigint[] | null;

interface UserFilterValueProps {
    value: UserFilterValue;
    onChange: (value: UserFilterValue) => void;
    operator?: FilterOperator;
    autoFocus?: boolean;
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}

// Factory function to create UserFilterValue with specific configuration
export const createUserFilterValue = (showUnallocated: boolean, showCurrentUser = false) => {
    return memo(function UserFilterValueWithConfig({
        value,
        onChange,
        operator = 'equals',
        getPopupContainer,
    }: UserFilterValueProps) {
        // Determine if we need multi-select based on operator
        const isMultiple = operatorSupportsArrayValue(operator);

        if (isMultiple) {
            // For multi-select operators (in, notIn), use UserSelectorMultiple
            const arrayValue: bigint[] = Array.isArray(value) ? value : value ? [value] : [];

            return (
                <UserSelectorMultiple
                    value={arrayValue}
                    onChange={(userIds: bigint[] | null) => onChange(userIds || [])}
                    placeholder="Select users"
                    className="min-w-[150px]"
                    size="medium"
                    maxTagCount={1}
                    limitToCurrentUserHandlingParties
                    showUnallocated={showUnallocated}
                    showCurrentUser={showCurrentUser}
                    getPopupContainer={getPopupContainer}
                />
            );
        }

        // For single-select operators (equals, not), use regular UserSelector
        const singleValue: bigint | null = Array.isArray(value) ? value[0] || null : value;

        return (
            <UserSelector
                value={singleValue}
                onChange={(userId: bigint | null) => onChange(userId)}
                placeholder="Select user"
                className="min-w-[150px]"
                allowClear
                maxTagCount="responsive"
                size="medium"
                limitToCurrentUserHandlingParties
                showUnallocated={showUnallocated}
                showCurrentUser={showCurrentUser}
                getPopupContainer={getPopupContainer}
            />
        );
    });
};

// Default UserFilterValue without showUnallocated
export const UserFilterValue = createUserFilterValue(false);
